/**
 * Auth modal — signup + login tabs in a single overlay. Optional invite
 * code field on signup unlocks the trial bonus when valid.
 */
import { useEffect, useState } from 'react';

interface AuthModalProps {
  initialTab: 'signup' | 'login';
  onSignup: (email: string, password: string, inviteCode?: string) => Promise<void>;
  onLogin: (email: string, password: string) => Promise<void>;
  onClose: () => void;
}

export function AuthModal({ initialTab, onSignup, onLogin, onClose }: AuthModalProps) {
  const [tab, setTab] = useState<'signup' | 'login'>(initialTab);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Close on Escape.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, busy]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (busy) return;
    setError(null);
    setBusy(true);
    try {
      if (tab === 'signup') {
        await onSignup(email.trim(), password, inviteCode.trim() || undefined);
      } else {
        await onLogin(email.trim(), password);
      }
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={busy ? undefined : onClose} role="dialog" aria-modal="true">
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal__tabs">
          <button
            type="button"
            className={`modal__tab${tab === 'signup' ? ' modal__tab--active' : ''}`}
            onClick={() => setTab('signup')}
            disabled={busy}
          >
            Sign up
          </button>
          <button
            type="button"
            className={`modal__tab${tab === 'login' ? ' modal__tab--active' : ''}`}
            onClick={() => setTab('login')}
            disabled={busy}
          >
            Sign in
          </button>
          <button
            type="button"
            className="modal__close"
            onClick={onClose}
            disabled={busy}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <form onSubmit={submit} className="modal__body">
          {tab === 'signup' && (
            <p className="modal__intro">
              Create a free Lites account — 10 generations/day, saved history, optional invite-code trial.
            </p>
          )}
          {tab === 'login' && (
            <p className="modal__intro">Welcome back.</p>
          )}

          <label className="modal__field">
            <span>Email</span>
            <input
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={busy}
            />
          </label>

          <label className="modal__field">
            <span>Password</span>
            <input
              type="password"
              autoComplete={tab === 'signup' ? 'new-password' : 'current-password'}
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={busy}
            />
            {tab === 'signup' && <small>At least 8 characters.</small>}
          </label>

          {tab === 'signup' && (
            <label className="modal__field">
              <span>
                Invite code <small className="modal__optional">(optional)</small>
              </span>
              <input
                type="text"
                value={inviteCode}
                onChange={(e) => setInviteCode(e.target.value)}
                placeholder="Have a code? Paste here"
                disabled={busy}
              />
              <small>Valid invite codes unlock a 7-day free Subscriber trial.</small>
            </label>
          )}

          {error && <div className="modal__error">{error}</div>}

          <button
            type="submit"
            className="button button--primary button--large modal__submit"
            disabled={busy || !email.trim() || password.length < 8}
          >
            {busy ? 'Working…' : tab === 'signup' ? 'Create account' : 'Sign in'}
          </button>

          <div className="modal__switch">
            {tab === 'signup' ? (
              <>
                Already have an account?{' '}
                <button type="button" onClick={() => setTab('login')} disabled={busy}>
                  Sign in
                </button>
              </>
            ) : (
              <>
                New to Lites?{' '}
                <button type="button" onClick={() => setTab('signup')} disabled={busy}>
                  Sign up
                </button>
              </>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
